'use client';

/**
 * Client Component for Donation Page
 *
 * Handles single-step donation flow with GoCardless redirect
 * Supports embed mode for iframe embedding on charity websites
 */

import { useEffect } from 'react';
import { DonorDetailsForm } from '@/components/donate/donor-details-form';
import { SandboxBadge } from '@/components/sandbox-badge';
import { reportHeight, postMessageToParent } from '@/lib/iframe/post-message';
import { cn } from '@/lib/utils';
import { brandCssVars } from '@/lib/branding/colour';

interface DonatePageClientProps {
  charityId: string;
  charityName: string;
  charityLogoUrl?: string | null;
  /** Charity brand colour (#rrggbb). Null keeps the TrusteeKit gold. */
  primaryColor?: string | null;
  customReturnUrl?: string | null;
  isEmbed?: boolean;
}

export function DonatePageClient({
  charityId,
  charityName,
  charityLogoUrl,
  primaryColor,
  customReturnUrl,
  isEmbed = false,
}: DonatePageClientProps) {
  // Handle embed mode setup
  useEffect(() => {
    if (isEmbed) {
      // Add embed-mode class to html and body for CSS overrides
      document.documentElement.classList.add('embed-mode');
      document.body.classList.add('embed-mode');

      // Signal that the donation form is ready
      postMessageToParent({ type: 'donation_ready' });

      // Report initial height
      reportHeight();

      // Observe size changes
      const observer = new ResizeObserver(() => {
        reportHeight();
      });
      observer.observe(document.body);

      return () => {
        document.documentElement.classList.remove('embed-mode');
        document.body.classList.remove('embed-mode');
        observer.disconnect();
      };
    }
  }, [isEmbed]);

  return (
    <div
      // The form reads --accent-gold* for every highlight; overriding them
      // here re-themes the whole form (and the processing overlay) from one
      // colour. Undefined when no colour is set, so globals.css gold applies.
      style={brandCssVars(primaryColor)}
      className={cn(
        isEmbed
          ? 'py-1 px-1 min-h-0 bg-transparent'
          : 'py-8 px-4 min-h-screen bg-(--warm-slate-50)'
      )}
    >
      <div className={cn('mx-auto', isEmbed ? 'w-full' : 'max-w-md')}>
        <SandboxBadge />
        <DonorDetailsForm
          charityId={charityId}
          charityName={charityName}
          charityLogoUrl={charityLogoUrl}
          customReturnUrl={customReturnUrl}
          isEmbed={isEmbed}
        />
      </div>
    </div>
  );
}
